import { IonIcon } from '@ionic/react';
import type { ComponentProps, MouseEvent as ReactMouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { chevronDownOutline } from 'ionicons/icons';
import { cn } from '../../../lib/utils';

type IconType = ComponentProps<typeof IonIcon>['icon'];

export interface ButtonsDropdownAction {
  label: string;
  icon: IconType;
  onClick: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  variant?: 'primary' | 'danger';
  disabled?: boolean;
  muted?: boolean;
  testId?: string;
}

interface ButtonsDropdownProps {
  actions: ButtonsDropdownAction[];
  label?: string;
  className?: string;
}

const ACTION_BASE_CLASSNAME = cn(
  'flex min-h-11 w-full items-center justify-center gap-2 rounded-[20px] border px-5 py-3 text-sm font-medium leading-none transition-colors',
  'focus:outline-none focus:ring-2 focus:ring-[var(--ion-color-primary)] focus:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

const ACTION_WRAPPER_CLASSNAME = 'w-fit min-w-[8.5rem] max-w-full sm:min-w-[240px] sm:max-w-[280px]';
const LONG_ACTION_WRAPPER_CLASSNAME = 'w-fit min-w-[13.5rem] max-w-full sm:min-w-[240px] sm:max-w-[280px]';
const ACTION_MENU_CLASSNAME = 'absolute right-0 top-full z-50 mt-2 flex max-h-52 w-full min-w-[13.5rem] max-w-[calc(100vw-2rem)] flex-col gap-2 overflow-y-auto rounded-[24px] border border-gray-200 bg-white p-3 shadow-lg';

const getActionClassName = (variant: ButtonsDropdownAction['variant']) => (
  variant === 'danger'
    ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
    : 'border-[color:rgba(var(--ion-color-primary-rgb),0.2)] bg-[rgba(var(--ion-color-primary-rgb),0.08)] text-[var(--ion-color-primary-shade)] hover:bg-[rgba(var(--ion-color-primary-rgb),0.14)]'
);

const ActionButton = ({ action }: { action: ButtonsDropdownAction }) => (
  <button
    type="button"
    onClick={(event) => {
      event.stopPropagation();
      action.onClick(event);
    }}
    disabled={action.disabled}
    className={cn(ACTION_BASE_CLASSNAME, getActionClassName(action.variant), action.muted && 'cursor-not-allowed opacity-50')}
    style={{ borderRadius: '20px' }}
    data-testid={action.testId}
  >
    <IonIcon icon={action.icon} className="shrink-0 text-base" />
    <span className="min-w-0 whitespace-nowrap text-center leading-tight">{action.label}</span>
  </button>
);

export const ButtonsDropdown = ({
  actions,
  label = 'Acciones',
  className,
}: ButtonsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  if (actions.length === 0) {
    return null;
  }

  if (actions.length === 1) {
    const wrapperClassName = actions[0].label.length > 14
      ? LONG_ACTION_WRAPPER_CLASSNAME
      : ACTION_WRAPPER_CLASSNAME;

    return (
      <div className={cn(wrapperClassName, className)}>
        <ActionButton action={actions[0]} />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn('relative', ACTION_WRAPPER_CLASSNAME, className)}
    >
      <button
        type="button"
        className={cn(
          ACTION_BASE_CLASSNAME,
          '!px-0 !py-0 overflow-hidden appearance-none',
          'justify-between border-gray-300 bg-slate-50 text-slate-700 shadow-sm hover:border-slate-400 hover:bg-white'
        )}
        style={{ borderRadius: '20px' }}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((current) => !current);
        }}
        aria-expanded={isOpen}
        data-testid="buttons-dropdown-trigger"
      >
        <div className="flex w-full items-center justify-between px-5 py-2">
          <span className="font-semibold">{label}</span>
          <div className="flex items-center justify-center rounded-full bg-white/90 p-1 shadow-sm">
            <IonIcon
              icon={chevronDownOutline}
              className={cn('text-base transition-transform', isOpen && 'rotate-180')}
            />
          </div>
        </div>
      </button>

      {isOpen && (
        <div
          className={ACTION_MENU_CLASSNAME}
          style={{ borderRadius: '24px' }}
          data-testid="buttons-dropdown-menu"
        >
          {actions.map((action) => (
            <ActionButton key={`${action.label}-${action.testId ?? 'action'}`} action={action} />
          ))}
        </div>
      )}
    </div>
  );
};
